import { INVENTORY_COLS, INVENTORY_ROWS, EQUIP_SLOTS } from '../constants';

/**
 * InventoryManager
 * ────────────────
 * Pure data model for the player's backpack and equipment.
 * No Phaser dependency – just plain JS.
 *
 * Item shape (MVP):
 *   { id: string, name: string, icon: string|null, slot: string|null }
 */
export class InventoryManager {
  constructor() {
    /** Fixed-size backpack grid */
    this.backpack = new Array(INVENTORY_COLS * INVENTORY_ROWS).fill(null);

    /** Equipment slots */
    this.equipment = {};
    for (const slot of EQUIP_SLOTS) {
      this.equipment[slot] = null;
    }
  }

  /* ── Backpack ────────────────────────────────────────────────── */

  /**
   * Add an item to the first empty backpack slot.
   * @returns {boolean} true if the item was added
   */
  addItem(item) {
    const idx = this.backpack.indexOf(null);
    if (idx === -1) return false;
    this.backpack[idx] = item;
    return true;
  }

  /**
   * Remove the item at a given backpack index.
   * @returns {object|null} the removed item
   */
  removeItem(index) {
    const item = this.backpack[index];
    this.backpack[index] = null;
    return item;
  }

  /* ── Equipment ───────────────────────────────────────────────── */

  /**
   * Equip an item from a backpack index into its slot.
   * If the slot is occupied, the existing item goes back to the backpack.
   * @returns {boolean} success
   */
  equipItem(backpackIndex) {
    const item = this.backpack[backpackIndex];
    if (!item || !item.slot) return false;

    const existing = this.equipment[item.slot];
    this.equipment[item.slot] = item;
    this.backpack[backpackIndex] = existing; // swap (or null)
    return true;
  }

  /**
   * Unequip an item from a slot back into the backpack.
   * @returns {boolean} success
   */
  unequipItem(slot) {
    const item = this.equipment[slot];
    if (!item) return false;

    const emptyIdx = this.backpack.indexOf(null);
    if (emptyIdx === -1) return false; // backpack full

    this.backpack[emptyIdx] = item;
    this.equipment[slot] = null;
    return true;
  }
}
